import { useEffect, useRef, EffectCallback } from 'react'

type TInstance = {
    cleanup: ReturnType<EffectCallback>
    cleanupPending: boolean
}

/**
 * Runs `effect` once per mounted component instance and its cleanup once on unmount - also under Strict Mode,
 * whose simulated unmount/remount would otherwise run the effect twice.
 *
 * The cleanup is deferred by a microtask: if the effect is re-attached synchronously (Strict Mode), the pending
 * cleanup is cancelled and the first run stays in place. A real unmount (or an Activity hide) runs the cleanup,
 * and a later re-attach runs the effect again.
 */
const useOnceEffect = (effect: EffectCallback) => {
    const instanceRef = useRef<TInstance>({ cleanup: undefined, cleanupPending: false })

    useEffect(() => {
        const instance = instanceRef.current
        if (instance.cleanupPending)
            instance.cleanupPending = false
        else
            instance.cleanup = effect()

        return () => {
            instance.cleanupPending = true
            queueMicrotask(() => {
                if (!instance.cleanupPending)
                    return

                instance.cleanupPending = false
                const { cleanup } = instance
                instance.cleanup = undefined
                if (typeof cleanup === 'function')
                    cleanup()
            })
        }
    }, [])
}

export default useOnceEffect
